import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Mail, Users, Shield, CheckCircle, XCircle, Loader2, LogIn, Lock, Eye, EyeOff } from 'lucide-react'
import api from '../services/api'
import { authService } from '../services/auth'

interface InviteDetails {
  id: string
  account_name: string
  inviter_email: string
  invitee_email?: string
  role: 'admin' | 'member'
  workspace_ids?: string[]
  expires_at: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  needs_password?: boolean
}

interface InvitePageProps {
  onLogin?: () => void
}

export default function Invite({ onLogin }: InvitePageProps) {
  // Extract inviteId from pathname since component is rendered outside Routes
  const location = useLocation()
  const inviteId = location.pathname.split('/invite/')[1]?.split('?')[0] || ''
  const navigate = useNavigate()
  const [invite, setInvite] = useState<InviteDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [isRegistering, setIsRegistering] = useState(false)
  const [name, setName] = useState('')
  
  // Setup token from URL (for new users who need to set password)
  const setupToken = new URLSearchParams(location.search).get('setup') || ''
  // Whether this is a new user who needs to set their password
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(!!setupToken)
  const [settingUpPassword, setSettingUpPassword] = useState(false)

  const fetchInviteDetails = useCallback(async () => {
    console.log('[Invite] Fetching invite details for:', inviteId)
    try {
      const baseUrl = (import.meta.env.VITE_API_URL || 'https://app.pongofin.com/api').replace(/\/api$/, '')
      const apiUrl = `${baseUrl}/api/invite/${inviteId}`
      console.log('[Invite] API URL:', apiUrl)
      const response = await fetch(apiUrl)
      console.log('[Invite] Response status:', response.status)
      const data = await response.json()
      console.log('[Invite] Response data:', data)
      
      if (!response.ok) {
        setError(data.error || 'Invito non trovato')
        return
      }
      
      setInvite(data.invite)
      
      // If the API says user needs password and we have a setup token, show password form
      if (data.invite?.needs_password && setupToken) {
        setNeedsPasswordSetup(true)
      }
    } catch (err) {
      console.error('[Invite] Fetch error:', err)
      setError('Errore nel caricamento dell\'invito')
    } finally {
      setLoading(false)
    }
  }, [inviteId, setupToken])

  useEffect(() => {
    console.log('[Invite] inviteId:', inviteId, 'setupToken:', setupToken ? 'present' : 'none')
    
    // Check for token in URL (from Google OAuth callback)
    const urlParams = new URLSearchParams(window.location.search)
    const tokenFromUrl = urlParams.get('token')
    
    if (tokenFromUrl) {
      // Save token and clean URL
      localStorage.setItem('access_token', tokenFromUrl)
      // Remove token from URL without reload
      const cleanUrl = window.location.pathname
      window.history.replaceState({}, '', cleanUrl)
      setIsLoggedIn(true)
      setNeedsPasswordSetup(false)
      if (onLogin) onLogin()
    } else if (!setupToken) {
      // Check if already logged in (only if not a setup flow)
      const token = authService.getToken()
      setIsLoggedIn(!!token)
    }

    // Fetch invite details
    if (inviteId) {
      fetchInviteDetails()
    } else {
      // No invite ID - show error
      setError('ID invito mancante')
      setLoading(false)
    }
  }, [inviteId, setupToken, onLogin, fetchInviteDetails])

  // Handle setting password + accepting invite in one step (for new users)
  const handleSetupAndAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')

    if (password.length < 6) {
      setLoginError('La password deve essere di almeno 6 caratteri')
      return
    }

    if (password !== confirmPassword) {
      setLoginError('Le password non coincidono')
      return
    }

    setSettingUpPassword(true)

    try {
      const baseUrl = (import.meta.env.VITE_API_URL || 'https://app.pongofin.com/api').replace(/\/api$/, '')
      const response = await fetch(`${baseUrl}/api/invite/${inviteId}/setup-and-accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setup_token: setupToken,
          password: password
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Errore durante la configurazione')
      }

      // Save token - user is now logged in
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token)
        console.log('[Invite] Setup complete, token saved')
        if (onLogin) onLogin()
      }

      // Navigate to the account page with success message
      navigate('/account?invite_accepted=true')
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Errore durante la configurazione')
    } finally {
      setSettingUpPassword(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    
    try {
      const result = await authService.login({ email, password })
      // Check if OTP is required
      if ('requires_otp' in result && result.requires_otp) {
        setLoginError('OTP richiesto. Accedi dalla pagina di login principale.')
        return
      }
      setIsLoggedIn(true)
      if (onLogin) onLogin()
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Credenziali non valide')
    }
  }

  const handleGoogleLogin = async () => {
    try {
      // Store invite ID to redirect back after login
      localStorage.setItem('pendingInviteId', inviteId || '')
      
      // Get the Google OAuth URL from the API
      const baseUrl = (import.meta.env.VITE_API_URL || 'https://app.pongofin.com/api').replace(/\/api$/, '')
      const response = await fetch(
        `${baseUrl}/api/auth/google?redirect_uri=${encodeURIComponent(window.location.origin + '/invite/' + inviteId)}`
      )
      const data = await response.json()
      
      if (data.url) {
        window.location.href = data.url
      } else {
        setLoginError('Errore durante la connessione a Google')
      }
    } catch {
      setLoginError('Errore durante la connessione a Google')
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    
    try {
      const baseUrl = (import.meta.env.VITE_API_URL || 'https://app.pongofin.com/api').replace(/\/api$/, '')
      const response = await fetch(`${baseUrl}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.error || 'Errore registrazione')
      }
      
      // Login after registration
      const loginResult = await authService.login({ email, password })
      if ('requires_otp' in loginResult && loginResult.requires_otp) {
        setLoginError('OTP richiesto. Accedi dalla pagina di login principale.')
        return
      }
      setIsLoggedIn(true)
      if (onLogin) onLogin()
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Errore registrazione')
    }
  }

  const acceptInvite = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/account/invite/${inviteId}/accept`)
      return response.data
    },
    onSuccess: () => {
      navigate('/account?invite_accepted=true')
    },
    onError: (err: Error) => {
      setError(err.message || 'Errore nell\'accettazione dell\'invito')
    }
  })

  const declineInvite = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/account/invite/${inviteId}/decline`)
      return response.data
    },
    onSuccess: () => {
      navigate('/')
    }
  })

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    )
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-[20px] shadow-elevated p-8 text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Invito non valido
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white h-[44px] px-6 rounded-[12px] hover:bg-blue-700 hover:-translate-y-px hover:shadow-btn-hover active:scale-[0.98] transition-all duration-200 ease-apple"
          >
            Torna alla home
          </button>
        </div>
      </div>
    )
  }

  if (invite?.status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-[20px] shadow-elevated p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Invito già accettato
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Fai già parte del team {invite.account_name}
          </p>
          <button
            onClick={() => navigate('/account')}
            className="bg-blue-600 text-white h-[44px] px-6 rounded-[12px] hover:bg-blue-700 hover:-translate-y-px hover:shadow-btn-hover active:scale-[0.98] transition-all duration-200 ease-apple"
          >
            Vai al tuo account
          </button>
        </div>
      </div>
    )
  }

  if (invite?.status === 'expired' || (invite && new Date(invite.expires_at) < new Date())) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-[20px] shadow-elevated p-8 text-center">
          <XCircle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Invito scaduto
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Questo invito è scaduto. Chiedi all'amministratore di inviarti un nuovo invito.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white h-[44px] px-6 rounded-[12px] hover:bg-blue-700 hover:-translate-y-px hover:shadow-btn-hover active:scale-[0.98] transition-all duration-200 ease-apple"
          >
            Torna alla home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-[20px] shadow-elevated overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 dark:bg-blue-700 p-6 text-white text-center">
          <Users className="h-12 w-12 mx-auto mb-3" />
          <h1 className="text-2xl font-bold">Invito al Team</h1>
        </div>

        {/* Content */}
        <div className="p-6">
          {invite && (
            <div className="space-y-4 mb-6">
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  Sei stato invitato a far parte di
                </p>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {invite.account_name}
                </h2>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Invitato da</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {invite.inviter_email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Ruolo</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {invite.role === 'admin' ? 'Amministratore' : 'Membro'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* FLOW 1: New user with setup token - Set password + accept in one step */}
          {needsPasswordSetup && setupToken ? (
            <div className="space-y-4">
              <div className="text-center">
                <Lock className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Imposta la tua password per accedere al team
                </p>
              </div>

              <form onSubmit={handleSetupAndAccept} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimo 6 caratteri"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Conferma Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Ripeti la password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                {loginError && (
                  <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">
                    {loginError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={settingUpPassword}
                  className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition disabled:opacity-50 font-medium"
                >
                  {settingUpPassword ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <CheckCircle className="h-5 w-5" />
                  )}
                  {settingUpPassword ? 'Configurazione in corso...' : 'Imposta password e accetta invito'}
                </button>
              </form>
            </div>
          ) : !isLoggedIn ? (
            /* FLOW 2: Existing user not logged in - Show login form */
            <div className="space-y-4">
              <p className="text-center text-gray-600 dark:text-gray-400 text-sm">
                Per accettare l'invito, accedi al tuo account
              </p>

              {/* Google Login */}
              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continua con Google
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">oppure</span>
                </div>
              </div>

              {!showLoginForm ? (
                <button
                  onClick={() => setShowLoginForm(true)}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition"
                >
                  <LogIn className="h-5 w-5" />
                  Accedi con email
                </button>
              ) : (
                <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-3">
                  {isRegistering && (
                    <input
                      type="text"
                      placeholder="Nome"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  )}
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                  
                  {loginError && (
                    <p className="text-red-500 text-sm">{loginError}</p>
                  )}
                  
                  <button
                    type="submit"
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    {isRegistering ? 'Registrati' : 'Accedi'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setIsRegistering(!isRegistering)}
                    className="w-full text-sm text-blue-600 hover:underline"
                  >
                    {isRegistering ? 'Hai già un account? Accedi' : 'Non hai un account? Registrati'}
                  </button>
                </form>
              )}
            </div>
          ) : (
            /* FLOW 3: User is logged in - Show accept/decline buttons */
            <div className="space-y-3">
              <button
                onClick={() => acceptInvite.mutate()}
                disabled={acceptInvite.isPending}
                className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {acceptInvite.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <CheckCircle className="h-5 w-5" />
                )}
                Accetta invito
              </button>
              <button
                onClick={() => declineInvite.mutate()}
                disabled={declineInvite.isPending}
                className="w-full flex items-center justify-center gap-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                {declineInvite.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <XCircle className="h-5 w-5" />
                )}
                Rifiuta
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
